import { useState, useEffect } from 'react';
import { filter } from 'lodash';
import axios from 'axios';
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
} from '@mui/material';

import Iconify from '../components/Iconify';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { HrconclaveListToolbar, HrconclaveListHead, HrconclaveMoreMenu } from '../sections/reports';

/* ---------------- TABLE HEAD ---------------- */

const TABLE_HEAD = [
  { id: 'mainFacilities', label: 'Main Facilities', alignRight: false },
  { id: 'otherFacilities', label: 'Other Facilities', alignRight: false },
  { id: 'additionalInfo', label: 'Additional Info', alignRight: false },
  { id: 'action', label: 'Action', alignRight: false },
];

/* ---------------- SORT + FILTER ---------------- */

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilized = array.map((el, i) => [el, i]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  let filtered = stabilized.map((el) => el[0]);

  if (query) {
    const q = query.toLowerCase();
    filtered = filtered.filter(
      (item) =>
        item.mainFacilities.join(',').toLowerCase().includes(q) ||
        item.otherFacilities.join(',').toLowerCase().includes(q)
    );
  }

  return filtered;
}

/* ================= COMPONENT ================= */

export default function Facilities() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('mainFacilities');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [facilities, setFacilities] = useState([]);
  const [selected, setSelected] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);

  /* -------- FORM STATE -------- */

  const [mainFacilities, setMainFacilities] = useState('');
  const [otherFacilities, setOtherFacilities] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState([]);

  /* ---------------- FETCH ---------------- */

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      const res = await axios.get('https://cpccbe.org/api/facilities/getfacilities');
      setFacilities(res.data.data || res.data);
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  /* ---------------- MODALS ---------------- */

  const resetForm = () => {
    setMainFacilities('');
    setOtherFacilities('');
    setAdditionalInfo([]);
  };

  const handleOpenModal = (id = null) => {
    resetForm();
    setEditingId(id);
    setOpenModal(true);

    if (id) {
      const item = facilities.find((f) => f._id === id);
      if (item) {
        setMainFacilities(item.mainFacilities.join(', '));
        setOtherFacilities(item.otherFacilities.join(', '));
        setAdditionalInfo(item.additionalInfo || []);
      }
    }
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleView = (item) => {
    setSelectedFacility(item);
    setViewModalOpen(true);
  };

  /* ---------------- CRUD ---------------- */

  const handleCreate = async () => {
    try {
      await axios.post('https://cpccbe.org/api/facilities/createfacilities', {
        mainFacilities: mainFacilities.split(',').map((s) => s.trim()),
        otherFacilities: otherFacilities.split(',').map((s) => s.trim()),
        additionalInfo,
      });
      fetchFacilities();
      handleCloseModal();
    } catch (err) {
      console.error('Create error:', err);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`https://cpccbe.org/api/facilities/updatefacilities/${editingId}`, {
        mainFacilities: mainFacilities.split(',').map((s) => s.trim()),
        otherFacilities: otherFacilities.split(',').map((s) => s.trim()),
        additionalInfo,
      });
      fetchFacilities();
      handleCloseModal();
    } catch (err) {
      console.error('Update error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this facility?')) return;
    try {
      await axios.delete(`https://cpccbe.org/api/facilities/deletefacilities/${id}`);
      fetchFacilities();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  /* ---------------- ADDITIONAL INFO ---------------- */

  const addInfoRow = () => {
    setAdditionalInfo([...additionalInfo, { title: '', action: '', link: '' }]);
  };

  const updateInfo = (i, field, value) => {
    const arr = [...additionalInfo];
    arr[i][field] = value;
    setAdditionalInfo(arr);
  };

  const removeInfo = (i) => {
    const arr = [...additionalInfo];
    arr.splice(i, 1);
    setAdditionalInfo(arr);
  };

  /* ---------------- TABLE ---------------- */

  const filteredFacilities = applySortFilter(
    facilities,
    getComparator(order, orderBy),
    filterName
  );

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - filteredFacilities.length)
      : 0;

  const isNotFound = filteredFacilities.length === 0;

  /* ================= RENDER ================= */

  return (
    <Page title="Facilities">
      <Container>
        <Stack direction="row" justifyContent="space-between" mb={4}>
          <Typography variant="h4">Facilities</Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => handleOpenModal()}
          >
            New Facility
          </Button>
        </Stack>

        <Card>
          <HrconclaveListToolbar
            filterName={filterName}
            onFilterName={(e) => setFilterName(e.target.value)}
            numSelected={selected.length}
          />

          <Scrollbar>
            <TableContainer>
              <Table>
                <HrconclaveListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={facilities.length}
                />

                <TableBody>
                  {filteredFacilities
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow hover key={row._id}>
                        <TableCell>
                          <IconButton onClick={() => handleView(row)}>
                            <Iconify icon="eva:eye-fill" />
                          </IconButton>
                        </TableCell>

                        <TableCell>{row.mainFacilities.join(', ')}</TableCell>
                        <TableCell>{row.otherFacilities.join(', ')}</TableCell>
                        <TableCell>
                          {row.additionalInfo.map((i, idx) => (
                            <Typography key={idx} variant="caption" display="block">
                              {i.title} — {i.action}
                            </Typography>
                          ))}
                        </TableCell>

                        <TableCell align="right">
                          <HrconclaveMoreMenu
                            onEdit={() => handleOpenModal(row._id)}
                            onDelete={() => handleDelete(row._id)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}

                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredFacilities.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Card>

        {/* ---------- CREATE / EDIT MODAL ---------- */}

        <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
          <DialogTitle>{editingId ? 'Edit Facility' : 'New Facility'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Main Facilities (comma separated)"
              fullWidth
              margin="dense"
              value={mainFacilities}
              onChange={(e) => setMainFacilities(e.target.value)}
            />

            <TextField
              label="Other Facilities (comma separated)"
              fullWidth
              margin="dense"
              value={otherFacilities}
              onChange={(e) => setOtherFacilities(e.target.value)}
            />

            <Box mt={2}>
              <Typography variant="subtitle2">Additional Info</Typography>

              <Button size="small" onClick={addInfoRow}>
                Add Info
              </Button>

              {additionalInfo.map((info, i) => (
                <Box key={i} display="flex" gap={1} mt={1}>
                  <TextField
                    label="Title"
                    value={info.title}
                    onChange={(e) => updateInfo(i, 'title', e.target.value)}
                  />
                  <TextField
                    label="Action"
                    value={info.action}
                    onChange={(e) => updateInfo(i, 'action', e.target.value)}
                  />
                  <TextField
                    label="Link"
                    value={info.link}
                    onChange={(e) => updateInfo(i, 'link', e.target.value)}
                  />
                  <IconButton onClick={() => removeInfo(i)}>
                    <Iconify icon="eva:trash-2-outline" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            {editingId ? (
              <Button onClick={handleUpdate}>Update</Button>
            ) : (
              <Button onClick={handleCreate}>Create</Button>
            )}
          </DialogActions>
        </Dialog>

        {/* ---------- VIEW MODAL ---------- */}

        <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)}>
          <DialogTitle>Facility Details</DialogTitle>
          <DialogContent>
            {selectedFacility && (
              <>
                <Typography><b>Main:</b> {selectedFacility.mainFacilities.join(', ')}</Typography>
                <Typography><b>Other:</b> {selectedFacility.otherFacilities.join(', ')}</Typography>

                <Box mt={2}>
                  <Typography variant="subtitle2">Additional Info</Typography>
                  {selectedFacility.additionalInfo.map((i, idx) => (
                    <Typography key={idx}>
                      {i.title} — {i.action} — {i.link}
                    </Typography>
                  ))}
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
